import { Navigate, type RouteObject } from 'react-router-dom';

import { AppShellProviders } from '@/components/common/AppShellProviders';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProtectedRoute } from '@/features/auth/ProtectedRoute';

import { OrganizationDetailPage } from './pages/OrganizationDetailPage';
import { OrgMembersPage } from './pages/OrgMembersPage';
import { OrganizationsPage } from './pages/OrganizationsPage';

/**
 * Organizations feature routes (flat glob registry — `src/features/registry.ts`).
 *
 * Every protected feature wraps its own tree in
 * `<ProtectedRoute><AppShellProviders><AppLayout/></…></ProtectedRoute>`, so this
 * feature never registers `/` (the auth feature owns the dashboard index) and
 * never asks the shell to know it exists. The sidebar remounts across features —
 * expected with a flat registry.
 *
 * `/organizations/:orgId` is a layout route whose index redirects to `projects`
 * (design §5.1: the end user's most frequent destination is the project list).
 * The `projects` children of the same path prefix are contributed by the
 * projects feature's own tree; react-router keeps the orgs branch only for the
 * paths it can fully match (`members`), so the two trees never fight.
 */
export const routes: RouteObject[] = [
  {
    path: '/organizations',
    element: (
      <ProtectedRoute>
        <AppShellProviders>
          <AppLayout />
        </AppShellProviders>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <OrganizationsPage /> },
      {
        path: ':orgId',
        element: <OrganizationDetailPage />,
        children: [
          { index: true, element: <Navigate to="projects" replace /> },
          { path: 'members', element: <OrgMembersPage /> },
        ],
      },
    ],
  },
];
